import { useCallback } from "react";
import { useSelector } from "react-redux";

import { useLayerAttributeOptions } from "../../../hooks/attributes/useLayerAttributeOptions";
import { useCurrentLayerName } from "../../../hooks/layer/useCurrentLayer";
import { partialUpdateSymbol } from "../../../ducks/style/style";
import { useAction } from "../../../hooks/useAction";
import { getScaleSegments, getSelectedSegment, getStyleChilds } from "../../../ducks/style/selectors";
import { castScaleToResolution } from "../../../evergis/core/scale";
import { getSegmentIndex } from "../../../utils/style";
import { useSegmentStyle } from "../../../modules/Style/hooks/useSegmentStyle";
import { AttributeOption } from "../../../evergis/core/attributes/createAttributeOption";
import { CalculatedParameter, isClusterSingleObject, ParameterValue } from "../../../interfaces/style";
import { ClassificationParameter } from "../../../interfaces/classification";

export type CalculatedParameterCreator = (option: AttributeOption) => CalculatedParameter<ParameterValue>;

export const useCalculateByAttribute = (name: ClassificationParameter, creator: CalculatedParameterCreator) => {
  const updateSymbol = useAction(partialUpdateSymbol);
  const children = useSelector(getStyleChilds);
  const scaleSegments = useSelector(getScaleSegments);
  const selectedSegment = useSelector(getSelectedSegment);
  const layerName = useCurrentLayerName();
  const segmentStyle = useSegmentStyle();
  const options = useLayerAttributeOptions(layerName);

  const selectOption = useCallback(
    ([option]: AttributeOption[]) => {
      const segmentIndex = getSegmentIndex({ scaleSegments, selectedSegment });
      const resolution = castScaleToResolution([scaleSegments[segmentIndex], scaleSegments[segmentIndex + 1]]);
      const childIndex = children?.findIndex(
        ({ minResolution, maxResolution }) => !maxResolution || minResolution === resolution[0],
      );

      updateSymbol({
        symbol: { [name]: creator(option) },
        index: childIndex,
        isClusterSingleObject: isClusterSingleObject(segmentStyle?.symbol, name),
      });
    },
    [scaleSegments, selectedSegment, children, updateSymbol, name, creator, segmentStyle?.symbol],
  );

  return [options, selectOption] as const;
};
